import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, XCircle, HelpCircle } from 'lucide-react';

interface Chemical {
  id: string;
  chemical_name: string;
  cas_number: string | null;
  chemical_type: string;
  supplier: string | null;
  molecular_formula: string | null;
  molecular_weight: number | null;
  hazard_level?: 'low' | 'moderate' | 'high' | 'unknown';
}

interface ChemicalCardProps {
  chemical: Chemical;
}

const getHazardIcon = (level: string | undefined) => {
  switch (level) {
    case 'low':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'moderate':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'high':
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return <HelpCircle className="h-4 w-4 text-muted-foreground" />;
  }
};

const getHazardColor = (level: string | undefined) => {
  switch (level) {
    case 'low':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'moderate':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'high':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export const ChemicalCard = ({ chemical }: ChemicalCardProps) => {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg leading-tight">{chemical.chemical_name}</CardTitle>
          <div className="flex items-center gap-1">
            {getHazardIcon(chemical.hazard_level)}
          </div>
        </div>
        {chemical.cas_number && (
          <p className="text-sm text-muted-foreground">CAS: {chemical.cas_number}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">{chemical.chemical_type}</Badge>
          <Badge className={getHazardColor(chemical.hazard_level)}>
            {chemical.hazard_level || 'Unknown'} Hazard
          </Badge>
        </div>

        <div className="space-y-2 text-sm">
          {chemical.supplier && (
            <div>
              <span className="font-medium">Supplier:</span> {chemical.supplier}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};